/**
 * Sprint Redesign v1 · Phase 2+ · Baustein 3 (Do 03.09.2026, 16:45).
 *
 * Fokustyp-Spiegel für /mein-muster — Selbst-Reflexions-Anker.
 * Zeigt fünf Perspektiven auf den eigenen Fokustyp.
 *
 * Erstauslieferung Schritt 1: Eule vollständig.
 * Alle anderen Slugs → Platzhalter mit „Vollausbau folgt".
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { FOKUSTYP_SPIEGEL, SPIEGEL_META } from "@/lib/fokustypSpiegel";
import { FokustypIcon } from "@/components/redesign/FokustypIcon";
import StressmusterGrid from "@/components/StressmusterGrid";
import { getFokustypTestRoute } from "@/lib/testRouting";
import { GlossarLink } from "@/components/redesign/Glossar";
import SteckbriefKachel from "@/components/redesign/SteckbriefKachel";
import { getKernZitat } from "@/lib/fokustypKernZitate";
import ShareFokustypButtonV2 from "@/components/ShareFokustypButtonV2";

export default function FokustypSpiegel({ slug, user = null }) {
  const navigate = useNavigate();
  const meta = SPIEGEL_META[slug] || SPIEGEL_META.eule;
  const spiegel = FOKUSTYP_SPIEGEL[slug] || { ready: false };
  const ready = spiegel.ready;
  const testRoute = getFokustypTestRoute(user);

  return (
    <section
      className="space-y-6"
      data-testid="fokustyp-spiegel"
      data-slug={slug}
      data-ready={ready ? "true" : "false"}
    >
      {/* Header-Karte mit Marken-Icon + Name + Tagline */}
      <header className="rounded-2xl border-2 border-[var(--fts-primary-bright)] bg-white dark:bg-slate-900 p-6 flex items-start gap-5">
        <span className="flex-shrink-0" aria-hidden="true">
          <FokustypIcon slug={slug} size={64} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Dein dominierender Fokus-Typ
          </p>
          <h2
            className="mt-0.5 text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight"
            data-testid="fokustyp-spiegel-name"
          >
            Typ {meta.name}
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {meta.tagline}
          </p>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-snug">
            Dein <GlossarLink term="Fokustyp">Fokustyp</GlossarLink> zeigt dir, wie du wahrnimmst und reagierst — und wie du für dein <GlossarLink term="Gegenüber">Gegenüber</GlossarLink> lesbar wirst.
          </p>
          <button
            type="button"
            onClick={() => {
              // Sprint V19 Schicht 3 Komponente 1 (Boardroom-CoS Mo 07.09.2026 17:00)
              // — Test-Wiederholungs-Verhalten Option B: Bestaetigungs-Dialog.
              // User hat aktuell Fokus-Typ X, wird gewarnt bevor er ihn ueberschreibt.
              const confirmed = window.confirm(
                `Du hast aktuell Typ ${meta.name} als dominierenden Fokus-Typ. ` +
                `Wenn du den Test wiederholst und ein anderer Typ herauskommt, ` +
                `wird dein Profil aktualisiert. Wirklich fortfahren?`
              );
              if (confirmed) navigate(testRoute);
            }}
            data-testid="fokustyp-spiegel-test-wiederholen"
            data-current-fokustyp={slug}
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold text-sm px-4 py-2"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Test wiederholen
          </button>
        </div>
      </header>

      {/* Sprint V38 (So 15.09.2026) — Share-CTA im Spiegel-Header */}
      {ready && user?.primary_fokustyp && (
        <section
          className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm p-5"
          data-testid="spiegel-share-section"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
                Muster teilen
              </p>
              <p className="mt-0.5 text-sm text-slate-700 dark:text-slate-200 font-medium">
                Wer sollte deinen Fokustyp kennen?
              </p>
            </div>
            <ShareFokustypButtonV2
              fokustyp={slug}
              referralCode={user?.referral_code || null}
              context="mein_muster"
              testId="spiegel-share-btn"
              compact
            />
          </div>
        </section>
      )}

      {ready ? (
        <>
          <SteckbriefKachel
            category="verhaltensweisen"
            slug={slug}
            items={spiegel.verhaltensweisen}
            quote={getKernZitat(slug, "verhaltensweisen")}
            testId="spiegel-verhaltensweisen"
          />

          <SteckbriefKachel
            category="erwartungshaltung"
            slug={slug}
            items={spiegel.erwartungshaltung}
            quote={getKernZitat(slug, "erwartungshaltung")}
            testId="spiegel-erwartungshaltung"
          />

          <SteckbriefKachel
            category="erste_impulse"
            slug={slug}
            items={spiegel.erste_impulse}
            quote={getKernZitat(slug, "erste_impulse")}
            testId="spiegel-erste-impulse"
          />

          <SteckbriefKachel
            category="motivation"
            slug={slug}
            items={spiegel.motivation || []}
            description={"Was dich wirklich antreibt — jenseits von \u201Edas gehört sich so\u201C."}
            quote={getKernZitat(slug, "motivation")}
            testId="spiegel-motivation"
          />

          <SteckbriefKachel
            category="stressmuster"
            slug={slug}
            items={spiegel.stressmuster}
            description="Wenn der Druck steigt, greifst du typischerweise zu einem dieser drei Muster."
            quote={getKernZitat(slug, "stressmuster")}
            testId="spiegel-stressmuster"
            footer={
              <>
                {spiegel.stressmuster_tief && (
                  <div
                    className="space-y-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-4 mb-5"
                    data-testid="spiegel-stressmuster-tief"
                  >
                    <div data-testid="spiegel-stressmuster-tief-wann">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Wann passiert es</p>
                      <p className="mt-1 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{spiegel.stressmuster_tief.wann}</p>
                    </div>
                    <div data-testid="spiegel-stressmuster-tief-wie">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Wie es sich anfühlt</p>
                      <p className="mt-1 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{spiegel.stressmuster_tief.wie}</p>
                    </div>
                    <div data-testid="spiegel-stressmuster-tief-erste-hilfe">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">Erste Hilfe</p>
                      <p className="mt-1 text-sm text-slate-800 dark:text-slate-100 leading-relaxed font-medium">{spiegel.stressmuster_tief.erste_hilfe}</p>
                    </div>
                  </div>
                )}
                <div data-testid="spiegel-stressmuster-reflexion">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">
                    Jetzt du · Welches Muster erkennst du bei dir?
                  </p>
                  <StressmusterGrid />
                </div>
              </>
            }
          />

          <SteckbriefKachel
            category="werte"
            slug={slug}
            items={spiegel.werte}
            quote={getKernZitat(slug, "werte")}
            testId="spiegel-werte"
          />

          <SteckbriefKachel
            category="blindspots"
            slug={slug}
            items={spiegel.blindspots || []}
            description="Wo dein Fokustyp dich regelmäßig auf's falsche Gleis führt — ohne dass du es merkst."
            quote={getKernZitat(slug, "blindspots")}
            accentDot="amber"
            testId="spiegel-blindspots"
          />
        </>
      ) : (
        <div
          className="rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-6"
          data-testid="fokustyp-spiegel-platzhalter"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
            In Vorbereitung
          </p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 leading-snug">
            Der Vollausbau des {meta.name}-Spiegels (Verhaltensweisen, Erwartungen, erste Impulse, Stressmuster, Werte) folgt im nächsten Sprint. Solange kannst du bereits den Test wiederholen und deine drei Stressmuster unten reflektieren.
          </p>
        </div>
      )}
    </section>
  );
}
